import { pluralize } from '../utils/common'

export function configureDragAndDrop<TConfig>(
  dnd: HTMLElement,
  onFilesAccepted: (files: File[], config: TConfig) => void,
  onFilesRejected: (files: File[]) => void,
  config: TConfig,
) {
  const dndOverlay = dnd.querySelector('div.dnd-overlay') as HTMLElement
  const dndInput = dnd.querySelector('input') as HTMLInputElement

  const getAcceptedFileTypes = () => {
    return dndInput.getAttribute('accept')?.split(',')
  }

  const getAcceptedAndRejectedCount = (e: DragEvent) => {
    let [acceptedCount, rejectedCount] = [0, 0]

    const items = e.dataTransfer?.items || []
    const acceptedFileTypes = getAcceptedFileTypes()
    if (acceptedFileTypes) {
      for (const item of items) {
        if (acceptedFileTypes?.includes(item.type)) {
          acceptedCount += 1
        } else {
          rejectedCount += 1
        }
      }
    } else {
      acceptedCount = items.length
    }

    return [acceptedCount, rejectedCount]
  }

  const getAcceptedAndRejectedFiles = (files: FileList) => {
    let acceptedFiles: Array<File> = []
    const rejectedFiles: Array<File> = []

    const acceptedFileTypes = getAcceptedFileTypes()
    if (acceptedFileTypes) {
      for (const file of files) {
        if (acceptedFileTypes?.includes(file.type)) {
          acceptedFiles.push(file)
        } else {
          rejectedFiles.push(file)
        }
      }
    } else {
      acceptedFiles = Array.from(files)
    }

    return [acceptedFiles, rejectedFiles]
  }

  const preventDefault = (e: Event) => {
    e.preventDefault()
    e.stopPropagation()
  }

  const displayOverlay = () => {
    dndOverlay.style.display = 'flex'
  }

  const hideOverlay = () => {
    dndOverlay.style.display = 'none'
    dndOverlay.classList.remove('accepted', 'rejected')
  }

  const highlight = (e: Event) => {
    const [acceptedFiles, rejectedFiles] = getAcceptedAndRejectedCount(
      e as DragEvent,
    )

    if (rejectedFiles > 0) {
      dndOverlay.classList.remove('accepted')
      dndOverlay.classList.add('rejected')
    } else if (acceptedFiles > 0) {
      dndOverlay.classList.remove('rejected')
      dndOverlay.classList.add('accepted')
    }

    let label = ''
    if (acceptedFiles > 0) {
      label += `${pluralize(acceptedFiles, 'file')} accepted`
      if (rejectedFiles > 0) {
        label += ` and ${pluralize(rejectedFiles, 'file')} rejected`
      }
    } else if (rejectedFiles > 0) {
      label += `${pluralize(rejectedFiles, 'file')} rejected`
    }
    dndOverlay.innerHTML = label
  }

  const handleFiles = (files: FileList) => {
    const [acceptedFiles, rejectedFiles] = getAcceptedAndRejectedFiles(files)
    if (acceptedFiles.length > 0) {
      onFilesAccepted(acceptedFiles, config)
    }
    if (rejectedFiles.length > 0) {
      onFilesRejected(rejectedFiles)
    }
  }

  const onDrop = (e: Event) => {
    const files = (e as DragEvent).dataTransfer?.files
    if (files) {
      handleFiles(files)
    }
  }

  const onClick = () => {
    dndInput.value = ''
    dndInput.click()
  }

  const onChange = (e: Event) => {
    const files = (e.target as HTMLInputElement).files
    if (files) {
      handleFiles(files)
    }
  }

  ;['dragenter', 'dragover', 'dragleave', 'drop'].forEach((eventName) => {
    dnd.addEventListener(eventName, preventDefault, false)
    dndOverlay.addEventListener(eventName, preventDefault, false)
  })
  dnd.addEventListener('dragenter', displayOverlay, false)
  ;['dragenter', 'dragover'].forEach((eventName) => {
    dndOverlay.addEventListener(eventName, highlight, false)
  })
  ;['dragleave', 'drop'].forEach((eventName) => {
    dndOverlay.addEventListener(eventName, hideOverlay, false)
  })
  dndOverlay.addEventListener('drop', onDrop, false)
  dnd.addEventListener('click', onClick, false)
  dndInput.addEventListener('change', onChange, false)
}
